var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// eslint-disable-next-line max-classes-per-file
import { Buffer } from "buffer";
import { ModelResolver } from "./index";
// eslint-disable-next-line @typescript-eslint/naming-convention,camelcase
export class DefaultModelResolver__JSONLike {
    constructor(data) {
        this.__asJson = true;
        this.data = data;
    }
    toJSON() {
        return JSON.stringify(this.data);
    }
}
export default class DefaultModelResolver extends ModelResolver {
    static typeName() {
        return "DefaultModelResolver";
    }
    constructor(options) {
        super(options);
        const primitiveBuilder = (Type, isClass = true) => (value) => {
            if (isClass) {
                return new Type(value).valueOf();
            }
            return Type(value).valueOf();
        };
        this.primitiveValues = [
            "Boolean",
            "Number",
            "String",
            "BigInt",
            "Symbol",
            "Buffer",
        ];
        this.unsupportedTypes = ["Promise", "SharedArrayBuffer"];
        this.ignoredTypesFromExtendedCopy = ["Function", "Object"];
        this.ignoredTypes = {
            null: null,
            undefined,
        };
        this.genericTypes = {
            Boolean: primitiveBuilder(Boolean),
            Number: primitiveBuilder(Number),
            String: primitiveBuilder(String),
            Symbol: primitiveBuilder(Symbol, false),
            BigInt: primitiveBuilder(BigInt, false),
            Array,
            RegExp,
            Error,
            EvalError,
            RangeError,
            ReferenceError,
            SyntaxError,
            TypeError,
            URIError,
            Math,
            Set,
            Map,
            WeakSet,
            WeakMap,
            Int8Array,
            Uint8Array,
            Uint8ClampedArray,
            Int16Array,
            Uint16Array,
            Int32Array,
            Uint32Array,
            Float32Array,
            Float64Array,
            ArrayBuffer,
            DataView,
            Buffer: Buffer.from,
        };
        this.overloadedTypes = {
            Date: {
                serialize: (model) => __awaiter(this, void 0, void 0, function* () { return model.toISOString(); }),
                deserialize: (model) => __awaiter(this, void 0, void 0, function* () { return new Date(model.value); }),
            },
            Function: {
                deserialize: (model, getFunction) => __awaiter(this, void 0, void 0, function* () { return getFunction(model.id); }),
            },
            AsyncFunction: {
                deserialize: (model, getFunction) => __awaiter(this, void 0, void 0, function* () { return getFunction(model.id); }),
            },
            DefaultModelResolver__JSONLike: {
                serialize: (model) => __awaiter(this, void 0, void 0, function* () { return model.toJSON(); }),
                deserialize: (model) => __awaiter(this, void 0, void 0, function* () { return JSON.parse(model.value); }),
            },
        };
        this.typesModifier = {
            PassThrough: {
                serialize: (model) => __awaiter(this, void 0, void 0, function* () {
                    if (!model.__on) {
                        // eslint-disable-next-line no-param-reassign
                        model.__on = model.on;
                        // eslint-disable-next-line no-param-reassign
                        model.on = (...params) => model.__on(...params);
                    }
                }),
            },
            Function: {
                serialize: (model, getFunctionId) => __awaiter(this, void 0, void 0, function* () {
                    return ({
                        id: yield getFunctionId(model),
                    });
                }),
            },
            AsyncFunction: {
                serialize: (model, getFunctionId) => __awaiter(this, void 0, void 0, function* () {
                    return ({
                        id: yield getFunctionId(model),
                    });
                }),
            },
        };
    }
    serialize(affectedModel, getFunction) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const model = affectedModel;
            const returnValue = {};
            if (Object.values(this.ignoredTypes).indexOf(model) === -1) {
                returnValue.type = ((_a = model.constructor) === null || _a === void 0 ? void 0 : _a.name) || "Object";
                if (typeof model === "object" && model.__asJson) {
                    returnValue.type = "DefaultModelResolver__JSONLike";
                }
                if (this.unsupportedTypes.indexOf(returnValue.type) !== -1) {
                    throw new Error(`Unsupported type: ${returnValue.type}`);
                }
                if ((_b = this.overloadedTypes[returnValue.type]) === null || _b === void 0 ? void 0 : _b.serialize) {
                    returnValue.value = yield this.overloadedTypes[returnValue.type].serialize(model, getFunction);
                }
                else if (this.primitiveValues.indexOf(returnValue.type) === -1) {
                    returnValue.value = {};
                    if (this.typesModifier[returnValue.type]) {
                        const result = yield ((_d = (_c = this.typesModifier[returnValue.type]).serialize) === null || _d === void 0 ? void 0 : _d.call(_c, model, getFunction));
                        if (result && typeof result === "object") {
                            Object.keys(result).forEach((key) => (returnValue[key] = result[key]));
                        }
                    }
                    for (let i = 0; i < Object.getOwnPropertyNames(model).length; i++) {
                        const item = Object.getOwnPropertyNames(model)[i];
                        item !== "prototype" &&
                            (returnValue.value[item] = yield this.serialize(model[item], getFunction));
                    }
                    if ([
                        ...Object.keys(this.genericTypes),
                        ...this.ignoredTypesFromExtendedCopy,
                    ].indexOf(returnValue.type) === -1) {
                        for (let i = 0; i <
                            Object.getOwnPropertyNames(Object.getPrototypeOf(model))
                                .length; i++) {
                            const item = Object.getOwnPropertyNames(Object.getPrototypeOf(model))[i];
                            item !== "constructor" &&
                                (returnValue.value[item] = yield this.serialize(model[item], getFunction));
                        }
                    }
                }
                else {
                    returnValue.value = model;
                }
            }
            else {
                returnValue.type = `${model}`;
                returnValue.value = model;
            }
            return returnValue;
        });
    }
    deserialize(model, getFunction) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (Object.keys(this.ignoredTypes).indexOf(model.type) !== -1) {
                return model.value;
            }
            if ((_a = this.overloadedTypes[model.type]) === null || _a === void 0 ? void 0 : _a.deserialize) {
                return this.overloadedTypes[model.type].deserialize(model, getFunction);
            }
            if (this.primitiveValues.indexOf(model.type) !== -1) {
                return this.genericTypes[model.type](model.value);
            }
            let genericCreature;
            if (model.type === "Object") {
                genericCreature = {};
            }
            else if (Object.keys(this.genericTypes).indexOf(model.type) !== -1) {
                genericCreature = new this.genericTypes[model.type]();
            }
            else {
                const DynamicClass = class {
                };
                Object.defineProperty(DynamicClass, "name", { value: model.type });
                genericCreature = new DynamicClass();
            }
            for (let i = 0; i < Object.keys(model.value).length; i++) {
                const item = Object.keys(model.value)[i];
                genericCreature[item] = yield this.deserialize(model.value[item], getFunction);
            }
            return genericCreature;
        });
    }
    // eslint-disable-next-line camelcase
    static JSONLike(data) {
        return new DefaultModelResolver__JSONLike(data);
    }
}
//# sourceMappingURL=default.modelResolver.js.map